/**
 * Single source of truth for how a device/screen is labelled across the dashboard.
 *
 * Every callsite that needs to show a device name (dropdowns, cards, scheduler
 * rows, history tables) should call getDeviceDisplayName(device). Do NOT build
 * ad-hoc strings like `tv.tvId || tv.deviceId` — they bypass the fallback chain
 * and produce the ugly hardware-ID labels the admin originally complained about.
 *
 * Priority order:
 *   1. displayName            — user-set friendly name ("Sales Gallery TV")
 *   2. tvId                   — admin routing code, but only if different from deviceId
 *   3. location               — place label if nothing else is set
 *   4. category                — derived friendly name for known deviceId patterns
 *   5. "Screen ABCDEF"        — last-6 hex of deviceId as a stable fallback
 */
import { getScreenCategoryFromDeviceId, formatCategoryLabel, UNCATEGORIZED } from './screenCategories';

const isBlank = (s) => s == null || String(s).trim() === '';

/**
 * Heuristic: does this string look like an auto-populated hardware identifier
 * rather than a human-chosen name?
 *
 * Android TV boxes commonly register themselves with strings like:
 *   "H96_Max_M9"       (brand + model + revision)
 *   "MXQ_Pro_4K"
 *   "RK3576_9e4ed182"  (chipset + hex serial)
 *   "MT8167_abc123"
 *
 * These get auto-seeded into `tvId` or `location` by the TV app's register
 * flow, so the dashboard's fallback chain would otherwise show them as
 * "friendly" names — which defeats the whole point of having a displayName.
 *
 * We reject a string as a hardware id if ANY of these match:
 *  1. Starts with a known chipset/SoC prefix (RK, MT, MSM, QCS, S905, etc.)
 *  2. Ends with a 6+ char hex suffix (e.g. "_9e4ed182")
 *  3. Matches the "Brand_Word_Code" pattern of Android TV box names
 *     (word1_Capitalized_Code where Code has at least one digit)
 *  4. Is all-uppercase alphanumerics + underscores with ≥2 underscores
 *     (e.g. "MXQ_PRO_4K")
 *
 * Friendly names like "TV_LOBBY", "Main Hall", "Sales Gallery 1" all pass.
 */
export const looksLikeHardwareId = (str) => {
  if (isBlank(str)) return false;
  const s = String(str).trim();

  // 1. Chipset / SoC prefixes used by the most common Android TV box SoCs.
  //    These are almost never what a human would type as a name.
  if (/^(RK\d{3,}|MT\d{4,}|MSM\d{4,}|QCS\d{3,}|S\d{3,}[A-Z]?|SM\d{4,}|AML\d{3,})/i.test(s)) {
    return true;
  }

  // 2. Hex serial suffix ("_9e4ed182", "-abcdef123456")
  if (/[_-][a-f0-9]{6,}$/i.test(s)) return true;

  // 3. Android-TV-box model pattern: Brand_Part_Part_... with 2+ underscore
  //    segments and a digit somewhere. Matches "H96_Max_M9", "X96_Max_Plus",
  //    "T95_Z_Plus_2GB". Friendly names like "TV_LOBBY" (only 1 segment)
  //    or "Sales Gallery 1" (has a space) don't match.
  if (/^[A-Z][A-Za-z0-9]{1,5}(?:_[A-Za-z0-9]+){2,}$/.test(s) && /\d/.test(s)) {
    return true;
  }

  // 4. SHOUTY_SNAKE_WITH_MULTIPLE_UNDERSCORES (e.g. "MXQ_PRO_4K_2GB")
  if (/^[A-Z0-9_]+$/.test(s) && (s.match(/_/g) || []).length >= 2) {
    return true;
  }

  return false;
};

/**
 * Last 6 chars of the hardware id, uppercase, prefixed with "Screen".
 * Example: "RK3576_9e4ed182" -> "Screen 4ED182"
 */
const generatedFallback = (deviceId) => {
  const id = String(deviceId || '').replace(/[^A-Za-z0-9]/g, '');
  if (!id) return 'Unknown screen';
  const tail = id.slice(-6).toUpperCase();
  return `Screen ${tail}`;
};

/**
 * Return the primary display name for a device.
 * Pass either a device object from the API or { deviceId, tvId, ... }.
 */
export const getDeviceDisplayName = (device) => {
  if (!device) return 'Unknown screen';

  const deviceId = device.deviceId || device.tvId || '';

  if (!isBlank(device.displayName)) return String(device.displayName).trim();

  // Use tvId only if it's a real friendly alias — not the raw deviceId and
  // not a hardware model/serial that got auto-seeded during registration.
  const tvIdTrim = isBlank(device.tvId) ? '' : String(device.tvId).trim();
  if (
    tvIdTrim &&
    tvIdTrim !== String(deviceId).trim() &&
    !looksLikeHardwareId(tvIdTrim)
  ) {
    return tvIdTrim;
  }

  // Same treatment for location — some TV apps auto-fill it with the model name.
  const locationTrim = isBlank(device.location) ? '' : String(device.location).trim();
  if (locationTrim && !looksLikeHardwareId(locationTrim)) {
    return locationTrim;
  }

  // Try to derive from deviceId naming convention ("Sumadhura-Edition-G1-TV1" → "Sumadhura — Edition")
  const cat = formatCategoryLabel(getScreenCategoryFromDeviceId(deviceId));
  if (cat && cat !== UNCATEGORIZED) return cat;

  return generatedFallback(deviceId);
};

/**
 * Second line shown next to the name (small grey text).
 * Typically "Main Lobby · 3840×2160" or the raw deviceId when nothing else is set.
 *
 * Rules:
 * - Never repeats the primary display name
 * - Prefers location + native resolution
 * - Falls back to the short deviceId so admins can still copy it when needed
 */
export const getDeviceSubtitle = (device) => {
  if (!device) return '';
  const parts = [];
  const primary = getDeviceDisplayName(device);

  // Only include location if it's a human-entered place, not a hardware model name
  if (
    !isBlank(device.location) &&
    String(device.location).trim() !== primary &&
    !looksLikeHardwareId(device.location)
  ) {
    parts.push(String(device.location).trim());
  }

  // Prefer the profile-resolved dims (what content should be authored at)
  // over the raw EDID reading. For a portrait kiosk with a scaler, EDID
  // says 1920×1080 while the admin actually wants to know "1080×1920".
  // See backend cms.device.controller.js serializeDevice for how
  // profileContent is populated.
  const dims = device.profileContent || device.nativeResolution;
  if (dims && dims.width && dims.height) {
    parts.push(`${dims.width}×${dims.height}`);
  }

  // Always expose the raw deviceId as a last segment so it's still discoverable
  // (shortened when long to keep the line from overflowing).
  const id = device.deviceId || '';
  if (id && id !== primary) {
    const shortId = id.length > 16 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
    parts.push(shortId);
  }

  return parts.join(' · ');
};

/**
 * Compact one-line label for dropdowns: "Sales Gallery TV · 3840×2160".
 * Used by search/select widgets where there's only one line of text.
 * Prefers profileContent over nativeResolution (same rationale as
 * getDeviceSubtitle above).
 */
export const getDeviceCompactLabel = (device) => {
  const name = getDeviceDisplayName(device);
  const dims = device?.profileContent || device?.nativeResolution;
  if (dims?.width && dims?.height) {
    return `${name} · ${dims.width}×${dims.height}`;
  }
  return name;
};

/**
 * GCD via Euclid — used to simplify aspect ratios.
 */
const _gcd = (a, b) => {
  a = Math.abs(Math.round(a));
  b = Math.abs(Math.round(b));
  while (b) { [a, b] = [b, a % b]; }
  return a || 1;
};

/**
 * Simplify w:h to lowest terms, then snap near-standard ratios to their
 * canonical form so "1920x1080" reads "16:9" rather than "16:9" (already
 * simple) but also "2048x1152" reads "16:9" not "128:72". The snap uses a
 * 1% tolerance on the decimal ratio.
 */
const _simplifyRatio = (w, h) => {
  if (!w || !h) return null;
  const ratio = w / h;
  // Canonical ratios people actually recognise — check these first
  const canon = [
    { s: '16:9',  v: 16 / 9 },
    { s: '9:16',  v: 9 / 16 },
    { s: '4:3',   v: 4 / 3 },
    { s: '3:4',   v: 3 / 4 },
    { s: '21:9',  v: 21 / 9 },
    { s: '9:21',  v: 9 / 21 },
    { s: '32:9',  v: 32 / 9 },
    { s: '1:1',   v: 1 },
    { s: '16:10', v: 16 / 10 },
    { s: '10:16', v: 10 / 16 },
  ];
  // 2.5% tolerance snaps real-world panels to their marketing ratio:
  // 3440×1440 (2.389) and 2560×1080 (2.370) both read as "21:9" because
  // that's what the panel is sold as, even though the pixel ratio drifts
  // slightly from the ideal 2.333. The tolerance is still tight enough
  // that 16:9 (1.778) and 16:10 (1.600) are ~11% apart and never confused.
  for (const c of canon) {
    if (Math.abs(ratio - c.v) / c.v < 0.025) return c.s;
  }
  // Fall back to simplified fraction
  const g = _gcd(w, h);
  return `${w / g}:${h / g}`;
};

/**
 * Resolution label for the TVList subtitle. Shows exact pixels + aspect
 * ratio ("3840×2160 · 16:9") so a marketing exec knows both the target
 * size to upload and the orientation/shape of the creative without having
 * to do math. The ratio encodes orientation (16:9 landscape vs 9:16 portrait)
 * so no extra "portrait" suffix is needed.
 *
 * Examples:
 *   3840×2160  → "3840×2160 · 16:9"
 *   2160×3840  → "2160×3840 · 9:16"
 *   1920×1080  → "1920×1080 · 16:9"
 *   1080×1920  → "1080×1920 · 9:16"
 *   3440×1440  → "3440×1440 · 43:18"  (unusual — falls back to simplified)
 *   No native  → "Rockchip box" / "Android box" / etc (from deviceId prefix)
 *   Nothing    → null
 */
export const getResolutionLabel = (device) => {
  if (!device) return null;
  // Prefer profile content dims (what admins should author content at)
  // over the raw EDID reading. Falls back to EDID for unprofiled devices.
  const dims = device.profileContent || device.nativeResolution;
  if (dims && dims.width && dims.height) {
    const w = Number(dims.width);
    const h = Number(dims.height);
    if (!w || !h) return null;
    const ratio = _simplifyRatio(w, h);
    return ratio ? `${w}×${h} · ${ratio}` : `${w}×${h}`;
  }
  // No reported resolution — show a model hint from the deviceId prefix so
  // PENDING devices still have something meaningful on the row
  const id = String(device.deviceId || '').trim();
  if (!id) return null;
  const prefix = id.split(/[_\-\s]/)[0].toUpperCase();
  if (/^RK\d+$/.test(prefix)) return 'Rockchip box';
  if (/^MT\d+$/.test(prefix)) return 'MediaTek box';
  if (/^MSM\d+$/.test(prefix)) return 'Qualcomm box';
  if (/^S\d+[A-Z]?$/.test(prefix)) return 'Amlogic box';
  if (/^AML\d+$/.test(prefix)) return 'Amlogic box';
  if (/^H\d+$/.test(prefix) || /^X\d+$/.test(prefix) || /^T\d+$/.test(prefix)) return 'Android box';
  return null;
};

// Backend REST emits `lastSeenAt`; in-memory socket payloads emit `lastSeen`.
// Dashboard.jsx normalizes socket updates into `lastSeenAt`, so in practice
// `lastSeenAt` is always set. `lastSeen` fallback is for legacy/socket-raw
// paths only — every render sink should call this helper, never read directly.
export const getLastSeen = (device) =>
  device?.lastSeenAt ?? device?.lastSeen ?? null;
